/**
 * Convert any image File to JPEG via canvas.
 *
 * iOS Safari delivers HEIC/HEIF photos from the camera and photo library.
 * While Safari can render HEIC in `<img>`, other browsers (Chrome, Edge on
 * Android/desktop) cannot. Storing and serving HEIC blobs breaks cross-browser
 * photo display, and the AI vision endpoint may not decode HEIC correctly.
 *
 * This helper draws the image onto a canvas and exports it as JPEG. In Safari
 * (WebKit) this works for HEIC; in other browsers it works for all natively
 * supported formats. If the browser cannot decode the source (e.g. HEIC in
 * Chrome), `onerror` fires and the original file is returned unchanged so
 * upload still proceeds.
 */
export async function convertToJpeg(file: File, maxDimension = 1920): Promise<File> {
  // Already JPEG — return as-is (no quality loss from re-encoding).
  if (file.type === 'image/jpeg') return file;

  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);

      let width = img.naturalWidth;
      let height = img.naturalHeight;

      // Downscale very large images to keep upload payloads reasonable.
      if (width > maxDimension || height > maxDimension) {
        const ratio = maxDimension / Math.max(width, height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        resolve(file);
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(file);
            return;
          }
          const name = file.name.replace(/\.[^.]+$/, '.jpg');
          resolve(new File([blob], name, { type: 'image/jpeg' }));
        },
        'image/jpeg',
        0.85
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      // Browser cannot decode the source (e.g. HEIC in Chrome/Edge).
      // Upload the original file as-is — the server still accepts image/* types.
      resolve(file);
    };

    img.src = url;
  });
}
